interface DripEvent {
  event: string
  properties?: Record<string, any>
}

interface DripPlugin {
  track: (event: string, properties?: Record<string, any>) => void
}

interface DripConfig {
  SE?: string
  NO?: string
  DK?: string
}

interface NuxtAppWithI18n {
  $i18n: {
    locale: {
      value: string
    }
  }
}

interface RuntimeConfig {
  public: {
    drip: {
      SE?: string
      NO?: string
      DK?: string
    }
  }
}

declare global {
  interface Window {
    _dcq: any[]
    _dcs: {
      account?: string
    }
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  // Skip on server-side
  if (process.server) {
    return
  }

  const config = useRuntimeConfig()
  const isProd = process.env.NODE_ENV === 'production'

  const log = {
    info: (...args: any[]) => console.log('[Drip]', ...args),
    warn: (...args: any[]) => console.warn('[Drip]', ...args),
    error: (...args: any[]) => console.error('[Drip]', ...args)
  }

  const loadScript = (dripAccountId: string): Promise<void> => {
    if (process.server) return Promise.resolve()

    const scriptUrl = `https://tag.getdrip.com/${dripAccountId}.js`

    // Check if script is already loaded
    if (document.querySelector(`script[src*="getdrip.com/${dripAccountId}.js"]`)) {
      log.info('Script already loaded')
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      Object.assign(script, {
        type: 'text/javascript',
        async: true,
        defer: true,
        src: scriptUrl
      })

      script.addEventListener('load', () => {
        log.info('Script loaded successfully')
        resolve()
      })

      script.addEventListener('error', (error) => {
        log.error('Script failed to load', { dripAccountId, scriptUrl, error })
        reject(error)
      })

      document.body.appendChild(script)
      log.info('Script appended to body')
    })
  }

  const getDripAccountId = (): string | null => {
    // Get the current locale from the i18n instance
    const locale = (nuxtApp as unknown as NuxtAppWithI18n).$i18n.locale.value
    const countryCode = locale === 'sv' ? 'SE' : locale === 'no' ? 'NO' : 'DK'
    const dripConfig = config.public.drip as DripConfig | undefined
    return dripConfig?.[countryCode] || null
  }

  const initializeDrip = async (): Promise<boolean> => {
    if (process.server) return false

    const dripAccountId = getDripAccountId()
    if (!dripAccountId) {
      log.warn('No account ID found for country code')
      return false
    }

    try {
      window._dcq = window._dcq || []
      window._dcs = window._dcs || {}
      window._dcs.account = dripAccountId

      await loadScript(dripAccountId)
      return true
    } catch (error) {
      log.error('Failed to initialize Drip', error)
      return false
    }
  }

  const safeTrack = async (event: string, properties?: Record<string, any>): Promise<void> => {
    if (process.server) {
      log.info('Server side - skipping track', event)
      return
    }

    try {
      if (!window._dcq && !(await initializeDrip())) {
        return
      }

      window._dcq.push(['track', event, properties])
      log.info('Tracked event', event, properties)
    } catch (error) {
      log.error('Error tracking event:', error)
    }
  }

  // Initialize right away
  initializeDrip().catch(err => log.error('Error during initialization', err))

  // Return a different property name to avoid conflicts
  return {
    provide: {
      dripTracker: {
        track: safeTrack
      }
    }
  }
})
